// Auto-tile Adjacency Transitions
// Computes per-tile transition overlays to blend seams between adjacent tiles
// whose shared edge types differ. Stores results in renderMetaByCoord for the
// renderer to consume.

import { hexKey, getNeighbor, getEffectiveSide } from '../../math/hexMath.mjs';

// Opposite edge: OPP[i] = (i + 3) % 6
const OPP = [3, 4, 5, 0, 1, 2];

// Higher priority = this biome "owns" the seam and draws the blend overlay.
export const TILE_TYPE_PRIORITY = {
  ocean:      100,
  snow:        90,
  desert:      80,
  mountain:    70,
  deadplains:  60,
  grassland:   50,
  grass:       40,
};

// Module-level render cache: hexKey → { adjacencyTransitions: TransitionRun[] }
/** @type {Map<string, { adjacencyTransitions: Array<{minorType:string, X:number, Y:number, spriteKey:string|null}> }>} */
export const renderMetaByCoord = new Map();

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** FNV-1a 32-bit hash of a string. */
function _hash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Returns true if tile T at (qT, rT) owns the seam where tEdge meets nEdge.
 * Higher priority biome owns; deterministic hash breaks ties.
 */
function _isSeamOwner(qT, rT, tEdge, qN, rN, nEdge) {
  const pT = TILE_TYPE_PRIORITY[tEdge] ?? 0;
  const pN = TILE_TYPE_PRIORITY[nEdge] ?? 0;
  if (pT !== pN) return pT > pN;

  // Stable tie-break: sort keys, then hash
  const keyT = hexKey(qT, rT);
  const keyN = hexKey(qN, rN);
  const [minKey, maxKey] = keyT < keyN ? [keyT, keyN] : [keyN, keyT];
  const h = _hash(`${minKey}|${maxKey}`);
  return (h % 2 === 0) ? (keyT === minKey) : (keyT === maxKey);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Convert a boolean[6] edge mask to contiguous circular runs.
 * Handles the 5→0 wrap case.
 * @param {boolean[]} mask
 * @returns {Array<{X:number, Y:number}>}
 */
export function edgesMaskToRuns(mask) {
  const runs = [];
  let i = 0;
  while (i < 6) {
    if (!mask[i]) { i++; continue; }
    const start = i;
    while (i < 6 && mask[i]) i++;
    runs.push({ X: i - start, Y: start });
  }

  // Merge wrap: if edge 5 and edge 0 are both in runs, the last and first run
  // are actually contiguous (5 → 0 → …).
  if (mask[0] && mask[5] && runs.length >= 2) {
    const first = runs[0];
    const last  = runs[runs.length - 1];
    runs.shift();
    runs.pop();
    runs.unshift({ X: first.X + last.X, Y: last.Y });
  }

  return runs;
}

/**
 * Rebuild adjacency transition metadata for a single tile.
 *
 * @param {{ q: number, r: number }} coord
 * @param {Record<string, object>} tilesObj  plain { [hexKey]: tile } object
 * @param {Map<string, string>|null} tileTransitionKeys  "type:X:atlasY" → spriteKey
 */
export function rebuildRenderMetaAt(coord, tilesObj, tileTransitionKeys) {
  const { q, r } = coord;
  const key = hexKey(q, r);
  const tile = tilesObj[key];

  if (!tile || !tile.placed) {
    renderMetaByCoord.delete(key);
    return;
  }

  const rot = tile.rotation ?? 0;

  // Collect per-minorType edge masks
  /** @type {Map<string, boolean[]>} */
  const edgeMasks = new Map();

  for (let i = 0; i < 6; i++) {
    const tEdge = getEffectiveSide(tile.sides, rot, i);
    const nb = getNeighbor(q, r, i);
    const nKey = hexKey(nb.q, nb.r);
    const nTile = tilesObj[nKey];
    if (!nTile || !nTile.placed) continue;

    const nEdge = getEffectiveSide(nTile.sides, nTile.rotation ?? 0, OPP[i]);
    if (tEdge === nEdge) continue; // no seam

    if (!_isSeamOwner(q, r, tEdge, nb.q, nb.r, nEdge)) continue;

    // T owns the seam → draw nEdge type as overlay on T at edge i
    if (!edgeMasks.has(nEdge)) edgeMasks.set(nEdge, new Array(6).fill(false));
    edgeMasks.get(nEdge)[i] = true;
  }

  // Convert each minorType's mask into runs, then look up sprite keys
  const allRuns = [];
  for (const [minorType, mask] of edgeMasks) {
    for (const { X, Y } of edgesMaskToRuns(mask)) {
      // Atlas uses (Y + 2) % 6 offset vs procedural edge convention
      const atlasY = (Y + 2) % 6;
      const spriteKey = tileTransitionKeys?.get(`${minorType}:${X}:${atlasY}`) ?? null;
      allRuns.push({ minorType, X, Y, spriteKey });
    }
  }

  // Sort: largest runs first, then by higher priority type
  allRuns.sort((a, b) => {
    if (b.X !== a.X) return b.X - a.X;
    return (TILE_TYPE_PRIORITY[b.minorType] ?? 0) - (TILE_TYPE_PRIORITY[a.minorType] ?? 0);
  });

  // Clamp: max 3 runs, max 4 total edges
  const adjacencyTransitions = [];
  let totalEdges = 0;
  for (const run of allRuns) {
    if (adjacencyTransitions.length >= 3) break;
    if (totalEdges + run.X > 4) continue;
    adjacencyTransitions.push(run);
    totalEdges += run.X;
  }

  renderMetaByCoord.set(key, { adjacencyTransitions });
}

/**
 * Rebuild for coord and all 6 neighbours (call after any tile placement).
 */
export function rebuildRenderMetaAround(coord, tilesObj, tileTransitionKeys) {
  rebuildRenderMetaAt(coord, tilesObj, tileTransitionKeys);
  for (let i = 0; i < 6; i++) {
    const nb = getNeighbor(coord.q, coord.r, i);
    rebuildRenderMetaAt(nb, tilesObj, tileTransitionKeys);
  }
}

/**
 * Rebuild meta for all placed tiles (call after load / game reset).
 */
export function buildAllRenderMeta(tilesObj, tileTransitionKeys) {
  renderMetaByCoord.clear();
  for (const tile of Object.values(tilesObj)) {
    if (tile?.placed && tile.coord) {
      rebuildRenderMetaAt(tile.coord, tilesObj, tileTransitionKeys);
    }
  }
}

/** Clear all cached meta (game reset). */
export function clearAllRenderMeta() {
  renderMetaByCoord.clear();
}
